import { RARITY_ORDER, type InventoryItem, type ItemRarity } from '../net/protocol';
import { affixLine } from './compare';

export type { ItemRarity };
export { RARITY_ORDER };

export interface StashFilter {
  /** Free text, matched against name, base id and affix text. Case-insensitive. */
  search: string;
  /** Minimum rarity to highlight, or null for "any". */
  minRarity: ItemRarity | null;
}

export const EMPTY_FILTER: StashFilter = { search: '', minRarity: null };

/**
 * `affixText` is **gone**. It was a fourth copy of one decision — how an affix reads — and
 * its fallback printed the raw stat key, so an equipped item's tooltip said `+14 armor_flat`
 * while the identical item in the bag said `+14 Armour`. It was reported from a screenshot,
 * which is the only thing that could have caught it: the two paths render perfectly and
 * disagree, and every test of each one passed.
 *
 * `affixLine` in `compare.ts` is the one renderer, and its wording comes from the one table
 * that `config.StatKeys` is walked against. See `design/HUD-UX.md` §Reading an item.
 */


/**
 * Whether an item satisfies the filter.
 *
 * Pure and exported so the grid can dim non-matching slots rather than
 * re-rendering — the requirement in `doc/grind/design/HUD-UX.md`, and the
 * reason this lives outside the component.
 */
export function matchesFilter(item: InventoryItem, filter: StashFilter): boolean {
  if (filter.minRarity !== null) {
    const floor = RARITY_ORDER.indexOf(filter.minRarity);
    if (RARITY_ORDER.indexOf(item.rarity) < floor) return false;
  }

  const query = filter.search.trim().toLowerCase();
  if (query === '') return true;

  // The spec's own example: typing "stamina" finds items with stamina recovery,
  // so affix text has to be searchable, not just names.
  return (
    item.name.toLowerCase().includes(query) ||
    item.baseId.toLowerCase().includes(query) ||
    item.slot.toLowerCase().includes(query) ||
    item.affixes.some((a) => affixLine(a).toLowerCase().includes(query))
  );
}

/** How many items the filter picks out. */
export function countMatches(items: readonly InventoryItem[], filter: StashFilter): number {
  return items.reduce((n, item) => (matchesFilter(item, filter) ? n + 1 : n), 0);
}

/**
 * Sorts for display: best rarity first, then highest item level, then name.
 *
 * Deliberately not the server's order. The server sends pickup order, which is
 * chronological and useless for finding anything.
 */
export function sortForDisplay(items: readonly InventoryItem[]): InventoryItem[] {
  return [...items].sort((a, b) => {
    const rarity = RARITY_ORDER.indexOf(b.rarity) - RARITY_ORDER.indexOf(a.rarity);
    if (rarity !== 0) return rarity;
    if (b.itemLevel !== a.itemLevel) return b.itemLevel - a.itemLevel;
    return a.name.localeCompare(b.name);
  });
}

/** Lower-case rarity, for the CSS class suffix. */
export function rarityClass(rarity: ItemRarity): string {
  return rarity.toLowerCase();
}
